import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import _isEqual from 'lodash/isEqual';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

// Actions
import { moveEntity, moveSpecificEntity } from 'actions/website/EntityActions';

// Types
import type { ZolaFileResponseType } from 'actions/types/UploadcareActions';
import type { GenericEntityType } from 'actions/types/website/EntityActionTypes';
import type { SharedEntityProps } from '../types';

// Components
import EditPageSortableItem from './EditPageSortableItem';

// Styles
import './EditPageSortableList.less';
import { SortableListContainer } from './EditPageSortableList.styles';

type EditPageSortableListProps = {
  entities: { [key: number]: GenericEntityType };
  entityApiName?: string;
  entityDetailProps: SharedEntityProps;
  isMultiPhoto?: boolean;
  orderedIds: number[];
  pageType?: string;
  removePhotoFromMultiQueue?: (id: number) => void;
  updateMultiPhotosQueueIndexChange?: (
    oldIndex: number,
    newIndex: number,
    entityApiName?: string
  ) => void;
  updatePhotoCaptionInMultiQueue?: (id: number, newCaption: string) => void;
  updatePhotoInMultiQueue?: (id: number, newPhoto: ZolaFileResponseType) => void;
};

const EditPageSortableList = ({
  entities = {},
  entityApiName,
  entityDetailProps,
  isMultiPhoto,
  orderedIds = [],
  pageType,
  removePhotoFromMultiQueue,
  updateMultiPhotosQueueIndexChange,
  updatePhotoCaptionInMultiQueue,
  updatePhotoInMultiQueue,
}: EditPageSortableListProps): JSX.Element | null => {
  const dispatch = useDispatch();
  // dnd-kit wants ids as strings, but our API delivers them as numbers
  const [items, setItems] = useState(orderedIds.map(id => id.toString()));
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const resetItems = useCallback(() => setItems(orderedIds.map((num: number) => num.toString())), [
    orderedIds,
  ]);

  // since we transform the ids to strings and set state,
  // we have to manually watch for changes to the props and update accordingly
  useEffect(() => {
    if (!_isEqual(items.map(id => Number(id)).length, orderedIds.length)) resetItems();
  }, [items, orderedIds, resetItems]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setItems((ids: string[]) => {
        const oldIndex = ids.indexOf(active.id);
        const newIndex = ids.indexOf(over.id);
        if (isMultiPhoto && updateMultiPhotosQueueIndexChange) {
          updateMultiPhotosQueueIndexChange(Number(active.id), Number(over.id), entityApiName);
        }
        if (!isMultiPhoto) {
          if (pageType === 'home') {
            const homeStoryEntities = Object.keys(entities).map(key => Number(key));
            dispatch(moveSpecificEntity(oldIndex, newIndex, homeStoryEntities, entityApiName));
          } else {
            dispatch(moveEntity(oldIndex, newIndex, entityApiName));
          }
        }
        return arrayMove(ids, oldIndex, newIndex);
      });
    }
  };

  if (!items.length) return null;

  return (
    <SortableListContainer>
      <DndContext onDragEnd={handleDragEnd} sensors={sensors}>
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          {items.map(id => (
            <EditPageSortableItem
              key={id}
              id={id}
              entity={entities[Number(id)]}
              entityDetailProps={entityDetailProps}
              isMultiPhoto={isMultiPhoto}
              removePhotoFromMultiQueue={removePhotoFromMultiQueue}
              updatePhotoInMultiQueue={updatePhotoInMultiQueue}
              updatePhotoCaptionInMultiQueue={updatePhotoCaptionInMultiQueue}
            />
          ))}
        </SortableContext>
      </DndContext>
    </SortableListContainer>
  );
};

export default EditPageSortableList;
